import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Wrench, ShieldCheck } from "lucide-react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const nextParam = params.get("next") || "";
  const returnTo = nextParam || (location.state && location.state.from) || "/";

  if (user) return <Navigate to={returnTo} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username.trim().toLowerCase(), password);
      toast.success("Hoş geldiniz!");
      navigate(returnTo, { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background text-foreground">
      {/* Left visual */}
      <div className="hidden lg:flex relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/5252118/pexels-photo-5252118.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
          alt="Motor servis"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/90 via-slate-950/70 to-slate-950/40" />
        <div className="relative z-10 p-12 flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-md bg-white text-foreground flex items-center justify-center">
              <Wrench className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display font-bold text-xl leading-none tracking-tight">ERA Motor</div>
              <div className="text-[11px] text-muted-foreground/60 mt-1 uppercase tracking-widest">Servis Takip Sistemi</div>
            </div>
          </div>
          <div>
            <h1 className="font-display text-3xl xl:text-4xl font-bold leading-tight">
              Servisinizi <span className="text-blue-300">profesyonelce</span><br/>yönetin.
            </h1>
            <p className="mt-4 max-w-md text-slate-200 text-base leading-relaxed">
              Müşteri, motor ve iş emri takibi, fiyatlandırma, onay ve teslim süreçleri tek ekranda.
            </p>
            <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground/60">
              <ShieldCheck className="h-4 w-4" /> Güvenli iç kullanım sistemi
            </div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <Card className="w-full max-w-md p-7 shadow-sm border border-border bg-card">
          <div className="lg:hidden flex items-center gap-2.5 mb-6">
            <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
              <Wrench className="h-4 w-4" />
            </div>
            <div>
              <div className="font-display font-bold text-base leading-none">ERA Motor</div>
              <div className="text-[10px] text-muted-foreground mt-0.5 uppercase tracking-widest">Servis Takip Sistemi</div>
            </div>
          </div>
          <div>
            <span className="label-eyebrow">Giriş</span>
            <h2 className="font-display text-2xl font-bold mt-2">Hesabınıza giriş yapın</h2>
            <p className="text-sm text-muted-foreground mt-1">Servis kayıtlarınızı yönetmek için devam edin.</p>
          </div>
          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="username" className="mb-1.5 block">Kullanıcı Adı</Label>
              <Input
                id="username"
                data-testid="login-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                autoFocus
                required
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                inputMode="text"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Küçük harflerle yazın · admin / servis</p>
            </div>
            <div>
              <Label htmlFor="password" className="mb-1.5 block">Şifre</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full" data-testid="login-submit-button">
              {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
